/**
 * ONE DAY, IN FULL — the pane beside the grid.
 *
 * A calendar cell can hold three glyphs; a reader planning a piece needs the times, the summaries
 * and the status of their own work. This is where those go, and it is the reason the grid cells can
 * stay small enough to read as a month.
 *
 * ★ THREE SECTIONS, AND THE THIRD IS A COUNT AND NEVER A LIST. `dayLoad` is the owner's 2026-08-26
 * ruling (`docs/ui/04-pages.md`) in place of the week strip's *"Tarot for a Slow Week ·
 * 12:00 · Sana R."*: colleagues' forthcoming work is a far larger disclosure than the follower
 * COUNT an author is allowed, and the schema is explicit that this "can never become a list" —
 * "No item here carries an id, a title, an author or a time — there is no field to widen and no
 * cursor to follow" (`authors-api.yaml`). The wording below says that out loud, because
 * a bare number invites the question.
 *
 * ★ TIMES ARE RENDERED FROM THE INSTANT THE SERVER SENT, through `features/cabinet/format.ts`.
 * Nothing here computes "in 3 days": that would be a clock read, and this app has none.
 *
 * ★ DESIGN 15 (the owner's live look at real cabinets, 2026-10-01): the pane is a card, each sky
 * event and each of the author's pieces a small card inside it, and the day's categories the
 * prototype's dotted labels - "● Rituals and practice · 2" - wearing the same marks as the month's
 * legend (./category-marks.tsx). In plain words: no ids, no "exclusive" end, no priority CODE (a
 * known band is a word, an unknown one is left out), and the count is "across MoonX".
 */
import { useId } from 'react';
import type { ReactNode } from 'react';
import { Moon } from '@moonx/ui';
import { formatDayRange, formatInstant } from '../../cabinet/format';
import { useTranslation } from '../../../i18n';
import { StatusPill } from '../../cabinet/components';
import { categoryName, useCategoryLabels } from '../category-labels';
import { displayLocale } from '../domain/month';
import type { DayBucket } from './MonthGrid';
import { CategoryDot, categoriesIn, categoryMarks } from './category-marks';
import type { CategoryMark } from './category-marks';
import classes from './calendar.module.css';

export interface DayDetailProps {
  readonly bucket: DayBucket;
  readonly locale?: string;
  /**
   * The catalogue's names, when the screen already read them for the month's legend. Left out,
   * the pane reads them itself (`useCategoryLabels`), which is how it stands alone.
   */
  readonly labels?: ReadonlyMap<string, string>;
  /** The month's category marks, so a day's dots match the legend. Left out, the day's own. */
  readonly marks?: ReadonlyMap<string, CategoryMark>;
  /** The event groups' names (design 30, `useGroupNames`); left out, no group name is shown. */
  readonly groupNames?: ReadonlyMap<string, string>;
}

export function DayDetail(props: DayDetailProps) {
  return props.labels === undefined ? <WithOwnLabels {...props} /> : <DayPane {...props} />;
}

/** The pane standing alone - in a spec, or anywhere the month did not read the catalogue. */
function WithOwnLabels(props: DayDetailProps) {
  const labels = useCategoryLabels();
  return <DayPane {...props} labels={labels} />;
}

/** The sky event kind the moon's mark stands for (the same code ./MonthGrid.tsx reads). */
const LUNAR_PHASE_KIND = 'lunar_phase';

/**
 * The editorial priority band as a word. The bands are the managed lookup's (`editorial_priority`:
 * minor, notable, major, headline); a band this table does not know is left out rather than shown
 * as a code - it is a small word beside a title, and the title is the news.
 */
type Priority = 'headline' | 'major' | 'notable' | 'minor';
const PRIORITIES: ReadonlySet<string> = new Set<Priority>([
  'headline',
  'major',
  'notable',
  'minor',
]);

function isPriority(value: string): value is Priority {
  return PRIORITIES.has(value);
}

/** `2026-09-11` -> "Friday, September 11, 2026", on the day's own UTC calendar (design 24, T16). */
function fullDay(day: string, locale = displayLocale()): string {
  const parsed = new Date(`${day}T00:00:00.000Z`);
  return Number.isNaN(parsed.getTime())
    ? day
    : new Intl.DateTimeFormat(locale, { dateStyle: 'full', timeZone: 'UTC' }).format(parsed);
}

/**
 * The last day an all-day interval covers. `endsAt` is EXCLUSIVE (authors-api.yaml), so
 * the day before it - said as the last day rather than as "until … (exclusive)".
 */
function lastDayOf(endsAt: string): string | undefined {
  const parsed = new Date(`${endsAt.slice(0, 10)}T00:00:00.000Z`);
  return Number.isNaN(parsed.getTime())
    ? undefined
    : new Date(parsed.getTime() - 86_400_000).toISOString().slice(0, 10);
}

function DayPane({ bucket, locale, labels, marks, groupNames }: DayDetailProps) {
  const { t } = useTranslation('calendar');
  const headingId = useId();
  const scheduled = bucket.load?.scheduled ?? 0;
  const split = bucket.load?.byCategory ?? [];
  const known = labels ?? new Map<string, string>();
  const dayMarks = marks ?? categoryMarks(categoriesIn([{ byCategory: split }]), known);
  const moonPhase = bucket.events.some((event) => event.kind === LUNAR_PHASE_KIND);

  return (
    <section className={classes.pane} aria-labelledby={headingId}>
      <div className={classes.paneBody}>
        <h2 id={headingId} className={classes.paneTitle}>
          {fullDay(bucket.day, locale)}
          {moonPhase ? <Moon size={16} className={classes.moon} aria-hidden /> : null}
        </h2>

        {/* ---- the sky ------------------------------------------------------------------- */}
        <div className={classes.paneSection}>
          <PaneLabel>{t('day.sky')}</PaneLabel>
          {bucket.events.length === 0 ? (
            <Quiet>{t('day.noSky')}</Quiet>
          ) : (
            bucket.events.map((event) => {
              const priority =
                event.editorialPriority === null ||
                event.editorialPriority === undefined ||
                !isPriority(event.editorialPriority)
                  ? undefined
                  : t(`day.priority.${event.editorialPriority}`);
              const first = event.startsAt.slice(0, 10);
              const last =
                event.endsAt === null || event.endsAt === undefined
                  ? undefined
                  : lastDayOf(event.endsAt);
              // The group's name, unless the title already says it (a seeded event's title is it).
              const named =
                event.groupCode === null || event.groupCode === undefined
                  ? undefined
                  : groupNames?.get(event.groupCode);
              const group = named === event.title ? undefined : named;
              return (
                <div key={event.calendarEventId} className={classes.paneItem}>
                  <div className={classes.paneItemHead}>
                    <span className={classes.paneItemTitle}>
                      {event.kind === LUNAR_PHASE_KIND ? (
                        <Moon size={13} className={classes.moon} aria-hidden />
                      ) : null}
                      {event.title}
                    </span>
                    {priority === undefined ? null : (
                      <span className={classes.priority}>{priority}</span>
                    )}
                  </div>
                  <span className={classes.paneMeta}>
                    {/* `allDay` true means a client must render a DATE rather than an instant pair —
                        "a different rendering and not a rounding" (authors-api.yaml:5321-5323). And
                        a null `endsAt` is an INSTANT, not a zero-length interval (:5316-5318). */}
                    {event.allDay
                      ? last === undefined || last <= first
                        ? t('day.allDay')
                        : t('day.allDayRange', { range: formatDayRange(first, last, locale) })
                      : formatInstant(event.startsAt, locale)}
                    {group === undefined ? null : ` · ${group}`}
                  </span>
                  {event.summary === null || event.summary === undefined ? null : (
                    <span className={classes.paneText}>{event.summary}</span>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* ---- your own pieces ------------------------------------------------------------ */}
        <div className={classes.paneSection}>
          <PaneLabel>{t('day.yours')}</PaneLabel>
          {bucket.articles.length === 0 ? (
            <Quiet>{t('day.noneYours')}</Quiet>
          ) : (
            bucket.articles.map((article) => (
              <div
                key={article.articleId}
                className={`${classes.paneItem ?? ''} ${classes.paneItemOwn ?? ''}`}
              >
                <span className={classes.paneItemTitle}>{article.title}</span>
                <div className={classes.paneItemFoot}>
                  <StatusPill status={article.status} />
                  <span className={classes.paneMeta}>
                    {article.publishedAt !== null && article.publishedAt !== undefined
                      ? formatInstant(article.publishedAt, locale)
                      : article.goLiveAt !== null && article.goLiveAt !== undefined
                        ? formatInstant(article.goLiveAt, locale)
                        : t('day.noTime')}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>

        {/* ---- how crowded the slot is ---------------------------------------------------- */}
        <div className={classes.paneSection}>
          <PaneLabel>{t('day.busy')}</PaneLabel>
          <span className={classes.paneLead}>
            {scheduled === 0
              ? t('day.nothingScheduled')
              : scheduled === 1
                ? t('day.oneScheduled')
                : t('day.manyScheduled', { count: scheduled })}
          </span>
          <CategoryCounts split={split} marks={dayMarks} labels={known} />
          <Quiet>{t('day.countNote')}</Quiet>
        </div>
      </div>
    </section>
  );
}

/** A section label that is NOT a heading: three of them under one `<h2>` would make a four-level
 *  outline out of a card. The page's outline is `<h1>` on the screen and `<h2>` here. */
function PaneLabel({ children }: { children: string }) {
  return <span className={classes.paneLabel}>{children}</span>;
}

function Quiet({ children }: { readonly children: ReactNode }) {
  return <span className={classes.paneMeta}>{children}</span>;
}

/**
 * ★ THE DAY'S LOAD BY CATEGORY, BY THE CATALOGUE'S NAME (the owner's live walk of real cabinets,
 * 2026-10-01, read "PERSONALJOURNEY · 2"). The catalogue is ADMIN-managed and its codes are NOT all
 * lower-case, so a client must not pattern-match them (authors-api.yaml): the name is the
 * catalogue's own (`../category-labels.ts`), and a code it does not hold is printed verbatim, never
 * prettified into something that could collide. Each entry wears its category's mark, as in the
 * month's legend (design 15).
 */
function CategoryCounts({
  split,
  marks,
  labels,
}: {
  readonly split: NonNullable<NonNullable<DayBucket['load']>['byCategory']>;
  readonly marks: ReadonlyMap<string, CategoryMark>;
  readonly labels: ReadonlyMap<string, string>;
}) {
  const { t } = useTranslation('calendar');
  if (split.length === 0) {
    return null;
  }
  return (
    <ul className={classes.dotted} aria-label={t('day.byCategory')}>
      {split.map((one) => {
        const entry = marks.get(one.category);
        return (
          <li key={one.category} className={classes.dottedEntry}>
            {entry === undefined ? null : <CategoryDot mark={entry.mark} />}
            {categoryName(labels, one.category)} · {one.scheduled}
          </li>
        );
      })}
    </ul>
  );
}
